/* RE-ENTRY GUARD — this file must survive being evaluated more than once.
   LIVE BUG: snippets/gallery.liquid carries the <script src>, and sections/article-content.liquid
   renders that snippet inside a `for block in ... content_blocks` loop — so any article with two
   or more gallery blocks emitted the tag twice and the second gallery never initialised.
   The browser fetches a repeated <script src> once but EXECUTES it once per
   tag, and this file opens with top-level `class`/`const` declarations, which
   throw "Identifier ... has already been declared" on the SECOND evaluation —
   before any customElements.get() guard further down is ever reached. The whole
   file dies from that point, silently: no exception surfaces in the UI, the
   component simply never registers.
   Wrapping the file makes a second execution a no-op instead of a crash. Same
   defect and same fix as assets/header-localization.js. Do not unwrap this. */
if (!window.__blogGalleryLoaded) {
  window.__blogGalleryLoaded = true;

/* Blog gallery — native overflow scroller with a focal size shift + loop.
 *
 * --focus (0..1) is written per slide every animation frame from the slide's
 * distance to the snap point. CSS maps it to real size.
 *
 * Slide width is CONSTANT at both breakpoints (see gallery.css) and only the
 * media height animates, so the scroll axis never reflows and JS never has to
 * correct scrollLeft mid-gesture — native scroll-snap owns the snapping and
 * momentum. Which slide ends up smaller differs per breakpoint, but that is
 * purely a CSS concern; nothing here depends on it.
 *
 * What does differ between the two:
 *
 *   Mobile (< 750px): snap point is the track's leading edge, so a slide is
 *     aligned when its offsetLeft equals scrollLeft. Touch only.
 *
 *   Desktop (>= 750px): snap point is the track centre. Mouse drag pans
 *     scrollLeft; release settles onto the nearest slide. Measurements still
 *     use getBoundingClientRect (see _offsetToSnap).
 *
 * Infinite loop: clones of every original are inserted before and after.
 * When scrolling settles on a clone, we jump to the matching original at the
 * identical visual position.
 */

const MOBILE_MEDIA = '(max-width: 749px)';
const SCROLL_END_DELAY = 140; // ms — fallback for browsers without 'scrollend'
const DRAG_THRESHOLD = 8; // px — before a mousedown counts as a drag
const SETTLE_MAX_MS = 420; // desktop drag-release settle cap
const SETTLE_LAMBDA = 14; // desktop settle stiffness

class BlogGallery extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this.originals = Array.from(
      this.querySelectorAll(':scope > .blog-gallery__slide:not(.blog-gallery__slide--clone)')
    );
    if (this.originals.length === 0) {
      this._initialized = false;
      return;
    }

    this._loopEnabled = this.originals.length >= 2;
    this.classList.toggle('is-static', !this._loopEnabled);
    if (this._loopEnabled) this._cloneSlides();
    this.slides = Array.from(this.querySelectorAll('.blog-gallery__slide'));

    this._mobileQuery = window.matchMedia(MOBILE_MEDIA);
    this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._reduceMotion = this._motionQuery.matches;
    this._focusRaf = 0;
    this._settleRaf = 0;
    this._resizeRaf = 0;
    this._unmuteRaf = 0;
    this._dragResetTimer = 0;
    this._scrollEndTimer = 0;
    this._isVisible = false;
    this._dragging = false;
    this._didDrag = false;
    this._muted = false;
    this._activeSlide = null;
    this._supportsScrollEnd = 'onscrollend' in this;

    this._onScroll = () => this._handleScroll();
    this._onScrollEnd = () => this._handleScrollEnd();
    this._onResize = () => this._scheduleResize();
    this._onMotionChange = () => this._handleMotionChange();
    this._onClick = (event) => this._handleClick(event);
    this._onKeydown = (event) => this._handleKeydown(event);
    this._onPointerDown = (event) => this._handlePointerDown(event);
    this._onPointerMove = (event) => this._handlePointerMove(event);
    this._onPointerUp = (event) => this._handlePointerUp(event);
    this._onClickCapture = (event) => this._handleClickCapture(event);
    this._onIntersect = (entries) => {
      this._isVisible = entries[0].isIntersecting;
      if (this._isVisible) this._requestFocus();
    };

    for (const slide of this.slides) {
      const isClone = slide.classList.contains('blog-gallery__slide--clone');
      if (this._loopEnabled) {
        slide.addEventListener('click', this._onClick);
      }
      if (!isClone) {
        if (this._loopEnabled) {
          slide.setAttribute('tabindex', '0');
          slide.setAttribute('role', 'button');
          slide.addEventListener('keydown', this._onKeydown);
        } else {
          slide.removeAttribute('aria-pressed');
          slide.removeAttribute('role');
          slide.removeAttribute('tabindex');
        }
      }
    }

    this.addEventListener('scroll', this._onScroll, { passive: true });
    if (this._supportsScrollEnd) {
      this.addEventListener('scrollend', this._onScrollEnd);
    }
    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerup', this._onPointerUp);
    this.addEventListener('pointercancel', this._onPointerUp);
    this.addEventListener('click', this._onClickCapture, true);
    this._motionQuery.addEventListener('change', this._onMotionChange);
    this._resizeObserver = new ResizeObserver(this._onResize);
    this._resizeObserver.observe(this);

    this._io = new IntersectionObserver(this._onIntersect, { rootMargin: '100px' });
    this._io.observe(this);

    const rect = this.getBoundingClientRect();
    this._isVisible = rect.bottom > 0 && rect.top < window.innerHeight;

    this._measure();

    this._initRaf = requestAnimationFrame(() => {
      this._initRaf = 0;
      if (!this.isConnected) return;
      this._measure();
      const start = this._isMobile()
        ? this.originals[0]
        : this.originals[Math.floor((this.originals.length - 1) / 2)];
      this._jumpTo(start);
      this._updateFocus();
    });
  }

  disconnectedCallback() {
    if (!this._initialized) return;
    for (const slide of this.slides || []) {
      slide.removeEventListener('click', this._onClick);
      slide.removeEventListener('keydown', this._onKeydown);
    }
    this.removeEventListener('scroll', this._onScroll);
    if (this._supportsScrollEnd) {
      this.removeEventListener('scrollend', this._onScrollEnd);
    }
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerup', this._onPointerUp);
    this.removeEventListener('pointercancel', this._onPointerUp);
    this.removeEventListener('click', this._onClickCapture, true);
    if (this._motionQuery) this._motionQuery.removeEventListener('change', this._onMotionChange);
    if (this._io) this._io.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._initRaf) cancelAnimationFrame(this._initRaf);
    if (this._focusRaf) cancelAnimationFrame(this._focusRaf);
    if (this._settleRaf) cancelAnimationFrame(this._settleRaf);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    if (this._unmuteRaf) cancelAnimationFrame(this._unmuteRaf);
    clearTimeout(this._dragResetTimer);
    clearTimeout(this._scrollEndTimer);

    for (const clone of this.querySelectorAll('.blog-gallery__slide--clone')) {
      clone.remove();
    }
    for (const slide of this.originals) {
      slide.classList.remove('is-active');
      slide.removeAttribute('aria-pressed');
      slide.removeAttribute('role');
      slide.removeAttribute('tabindex');
      slide.style.removeProperty('--focus');
      delete slide._focusValue;
    }
    this.classList.remove('is-static');
    this._activeSlide = null;
    this._initialized = false;
  }

  _isMobile() {
    return this._mobileQuery.matches;
  }

  _cloneSlides() {
    const makeClone = (slide, index) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('blog-gallery__slide--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('aria-pressed');
      clone.removeAttribute('role');
      clone.setAttribute('tabindex', '-1');
      clone.dataset.cloneOf = String(index);
      const img = clone.querySelector('img');
      if (img) img.setAttribute('loading', 'lazy');
      return clone;
    };
    const before = this.originals.map(makeClone);
    const after = this.originals.map(makeClone);
    for (const clone of before) this.insertBefore(clone, this.originals[0]);
    for (const clone of after) this.appendChild(clone);
  }

  /* Snap-point pitch and one clone-set span. Both are exact constants at
     either breakpoint, because slide width never changes. */
  _measure() {
    const [first, second] = this.slides;
    this._step =
      second && second.offsetLeft > first.offsetLeft
        ? second.offsetLeft - first.offsetLeft
        : this.clientWidth || 1;
    this._loopSpan = this.originals.length * this._step;
    this._focusRange = this._isMobile() ? this._step : this._slideWidth();
  }

  _slideWidth() {
    const raw = getComputedStyle(this).getPropertyValue('--slide-w').trim();
    if (!raw) return this._step;
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;left:0;top:0;visibility:hidden;width:${raw};pointer-events:none`;
    this.appendChild(probe);
    const width = probe.getBoundingClientRect().width || this._step;
    probe.remove();
    return width;
  }

  // ----- Focus (size) -------------------------------------------------------

  _requestFocus() {
    if (this._focusRaf || !this._isVisible) return;
    this._focusRaf = requestAnimationFrame(() => {
      this._focusRaf = 0;
      this._updateFocus();
    });
  }

  /* Read-only: never writes scrollLeft. That is what keeps native snap and
     momentum scrolling smooth on touch. */
  _updateFocus() {
    if (!this._isVisible) return;
    const slides = this.slides;
    if (!slides || slides.length === 0) return;
    if (this._reduceMotion) {
      this._setActive(this._closest());
      return;
    }

    const mobile = this._isMobile();
    const range = this._focusRange || 1;
    const scrollLeft = this.scrollLeft;
    const trackRect = mobile ? null : this.getBoundingClientRect();
    const snapPoint = trackRect ? trackRect.left + trackRect.width / 2 : 0;

    // Read every position before writing any --focus value. Mixing reads and
    // writes in one loop forces repeated layout and biases later slides.
    const focusValues = [];
    for (const slide of slides) {
      let distance;
      if (mobile) {
        distance = Math.abs(slide.offsetLeft - scrollLeft);
      } else {
        const slideRect = slide.getBoundingClientRect();
        distance = Math.abs(slideRect.left + slideRect.width / 2 - snapPoint);
      }
      const t = Math.min(1, Math.max(0, 1 - distance / range));
      focusValues.push(Math.round(t * t * (3 - 2 * t) * 1000) / 1000);
    }

    let best = slides[0];
    let bestFocus = -1;
    for (const [i, slide] of slides.entries()) {
      const focus = focusValues[i];
      if (slide._focusValue !== focus) {
        slide._focusValue = focus;
        slide.style.setProperty('--focus', String(focus));
      }
      if (focus > bestFocus) {
        bestFocus = focus;
        best = slide;
      }
    }

    this._setActive(best);
  }

  _setActive(activeSlide) {
    if (this._activeSlide === activeSlide) return;
    this._activeSlide = activeSlide;
    for (const slide of this.slides) {
      slide.classList.toggle('is-active', slide === activeSlide);
    }

    /* aria-pressed is only valid on the role="button" slides, which exist just
       when the loop is on. A lone slide is inert, so leave it bare. */
    if (!this._loopEnabled) return;

    const activeOriginalIndex = activeSlide.classList.contains('blog-gallery__slide--clone')
      ? Number(activeSlide.dataset.cloneOf)
      : this.originals.indexOf(activeSlide);
    for (const [i, slide] of this.originals.entries()) {
      slide.setAttribute('aria-pressed', i === activeOriginalIndex ? 'true' : 'false');
    }
  }

  _scheduleResize() {
    if (this._resizeRaf) return;
    this._resizeRaf = requestAnimationFrame(() => {
      this._resizeRaf = 0;
      const activeSlide = this._activeSlide;
      this._measure();
      if (activeSlide && !this._dragging) {
        this._jumpTo(activeSlide);
      } else {
        this._requestFocus();
      }
    });
  }

  _handleMotionChange() {
    this._reduceMotion = this._motionQuery.matches;
    if (this._reduceMotion) {
      for (const slide of this.slides) {
        slide.style.removeProperty('--focus');
        delete slide._focusValue;
      }
    }
    this._updateFocus();
  }

  // ----- Scroll lifecycle ---------------------------------------------------

  _handleScroll() {
    if (this._muted) return;
    this._requestFocus();
    if (!this._supportsScrollEnd) {
      clearTimeout(this._scrollEndTimer);
      this._scrollEndTimer = setTimeout(this._onScrollEnd, SCROLL_END_DELAY);
    }
  }

  _handleScrollEnd() {
    clearTimeout(this._scrollEndTimer);
    if (this._muted || this._dragging || this._settleRaf) return;
    this._relocate();
    this._updateFocus();
  }

  /* Keep the scroll position inside the originals band so the track can be
     swiped forever. Runs only when motion has stopped, so it is invisible. */
  _relocate() {
    if (!this._loopEnabled) return;

    if (this._isMobile()) {
      // Leading clones occupy one span; wrap by exactly that amount.
      const span = this._loopSpan;
      let next = this.scrollLeft;
      if (next >= 2 * span - 1) next -= span;
      else if (next < span - 1) next += span;
      if (next !== this.scrollLeft) this._writeScroll(next);
      return;
    }

    const closest = this._closest();
    if (!closest || !closest.classList.contains('blog-gallery__slide--clone')) return;
    const original = this.originals[Number(closest.dataset.cloneOf)];
    if (!original) return;

    // Carry the clone's size over first, then land the original exactly on the
    // snap point. Only height differs between the two now, which cannot move a
    // horizontal snap point, so this is belt-and-braces rather than load-bearing.
    const focus = closest._focusValue ?? 1;
    original._focusValue = focus;
    original.style.setProperty('--focus', String(focus));
    this._writeScroll(this.scrollLeft + this._offsetToSnap(closest));
    this._writeScroll(this.scrollLeft + this._offsetToSnap(original));
    this._updateFocus();
    this._writeScroll(this.scrollLeft + this._offsetToSnap(original));
  }

  /* Snap-suppressed write, so the browser does not animate a correction. */
  _writeScroll(target) {
    this._muted = true;
    const prevSnap = this.style.scrollSnapType;
    this.style.scrollSnapType = 'none';
    this.scrollLeft = target;
    void this.offsetHeight; // flush, so the new position counts as settled
    this.style.scrollSnapType = prevSnap;
    if (this._unmuteRaf) cancelAnimationFrame(this._unmuteRaf);
    this._unmuteRaf = requestAnimationFrame(() => {
      this._unmuteRaf = 0;
      this._muted = false;
    });
  }

  // ----- Targeting ----------------------------------------------------------

  _offsetToSnap(slide) {
    if (this._isMobile()) return slide.offsetLeft - this.scrollLeft;
    const trackRect = this.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return (
      slideRect.left + slideRect.width / 2 - (trackRect.left + trackRect.width / 2)
    );
  }

  _closest() {
    let closest = this.slides[0];
    let closestDist = Infinity;
    for (const slide of this.slides) {
      const dist = Math.abs(this._offsetToSnap(slide));
      if (dist < closestDist) {
        closestDist = dist;
        closest = slide;
      }
    }
    return closest;
  }

  _jumpTo(slide) {
    if (!slide) return;
    this._writeScroll(this.scrollLeft + this._offsetToSnap(slide));
    this._updateFocus();
  }

  /* Mobile hands the animation to the browser: one native smooth scroll to an
     exact snap point, no per-frame scrollLeft writes. */
  _glideTo(slide) {
    if (!slide) return;
    if (this._reduceMotion) {
      this._jumpTo(slide);
      return;
    }
    if (this._isMobile()) {
      this.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      return;
    }
    this._settleTo(slide);
  }

  /* Desktop only — sizes change as we move, so re-measure every frame. */
  _settleTo(slide) {
    if (this._settleRaf) cancelAnimationFrame(this._settleRaf);
    this.style.scrollSnapType = 'none';

    const started = performance.now();
    let last = started;

    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.048);
      last = now;
      this._updateFocus();
      const remaining = this._offsetToSnap(slide);
      this.scrollLeft += remaining * (1 - Math.exp(-SETTLE_LAMBDA * dt));

      const done =
        Math.abs(this._offsetToSnap(slide)) < 0.75 || now - started > SETTLE_MAX_MS;
      if (!done) {
        this._settleRaf = requestAnimationFrame(tick);
        return;
      }
      this._settleRaf = 0;
      this.scrollLeft += this._offsetToSnap(slide);
      this.style.scrollSnapType = '';
      this._relocate();
      this._updateFocus();
    };
    this._settleRaf = requestAnimationFrame(tick);
  }

  // ----- Input --------------------------------------------------------------

  _handleClick(event) {
    if (this._didDrag) return;
    if (this._isMobile()) {
      // Advance by one, matching the "always scroll by 1" model.
      const index = this.slides.indexOf(this._closest());
      this._glideTo(this.slides[index + 1] || this.originals[0]);
      return;
    }
    this._glideTo(event.currentTarget);
  }

  _handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this._glideTo(event.currentTarget);
  }

  /* Mouse only. Touch and trackpad keep the browser's native panning. */
  _handlePointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this._isMobile()) return;
    this._pointerId = event.pointerId;
    this._dragStartX = event.clientX;
    this._dragStartScroll = this.scrollLeft;
    this._didDrag = false;
    this._dragging = true;
    this.setPointerCapture(event.pointerId);
  }

  _handlePointerMove(event) {
    if (!this._dragging || event.pointerId !== this._pointerId) return;
    const dx = event.clientX - this._dragStartX;
    if (!this._didDrag && Math.abs(dx) < DRAG_THRESHOLD) return;
    this._didDrag = true;
    this.classList.add('is-dragging');
    this.scrollLeft = this._dragStartScroll - dx;
  }

  _handlePointerUp(event) {
    if (event.pointerId !== this._pointerId) return;
    this._dragging = false;
    this._pointerId = null;
    this.classList.remove('is-dragging');
    if (this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }
    if (this._didDrag) this._glideTo(this._closest());
    clearTimeout(this._dragResetTimer);
    this._dragResetTimer = setTimeout(() => {
      this._didDrag = false;
    }, 0);
  }

  _handleClickCapture(event) {
    if (!this._didDrag) return;
    event.preventDefault();
    event.stopPropagation();
    this._didDrag = false;
  }
}

if (!customElements.get('blog-gallery')) {
  customElements.define('blog-gallery', BlogGallery);
}

} /* end re-entry guard */
